import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import OptimizedButton from "../../../components/agent/UX/OptimizedButton";
import LoadingSpinner from "../../../components/agent/UX/LoadingSpinner";
import CreateTripModal from "../../../components/agent/trips/CreateTripModal";
import PixieDustAnimation from "../../../components/agent/PixieDustAnimation";
import usePixieDustNavigation from "../../../hooks/usePixieDustNavigation";
import { cleanupOnNavigation } from "../../../utils/tabCleanup";
import useErrorHandler from "../../../hooks/useErrorHandler";
import useOptimizedFetch from "../../../hooks/useOptimizedFetch";
import {
  Search,
  Calendar,
  Users,
  TrendingUp,
  Sparkles,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  ArrowRight,
  FileText,
  Settings,
  Info,
  DollarSign,
  Target,
  Award,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Loader2,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import "../../../components/agent/crm/CRMDashboard.css";
import toast from "../../../utils/toast";
import { useUser } from "../../../context/UserContext";
import { hasFeature } from "../../../data/pricing";
import UsageDashboard from "../../../components/agent/UsageDashboard";
import useItineraryLimits from "../../../hooks/useItineraryLimits";
import { decodeHtmlEntities } from "../../../utils/textUtils";
import Topbar from "../../../components/agent/layout/Topbar";
import SidebarV2 from "../../../components/navigation/SidebarV2";

export default function UnifiedDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeWithErrorHandling } = useErrorHandler();
  const [showPendingTripModal, setShowPendingTripModal] = useState(false);
  const [selectedPendingTrip, setSelectedPendingTrip] = useState(null);
  const [showCreateTripModal, setShowCreateTripModal] = useState(false);

  // Pixie dust navigation hook
  const {
    showAnimation,
    animationDuration,
    navigateToUnifiedItinerary,
    handleAnimationComplete
  } = usePixieDustNavigation();

  const { user, loadingUser } = useUser();
  const agentId = user?.agency_id;
  const agencyPlan = agentId?.AgencyPlan || user?.planId || null;
  const canSharePublicForm =
    agencyPlan &&
    ["agentplus", "agency", "agencyplus", "enterprise"].includes(
      agencyPlan.toLowerCase()
    );

  // Itinerary limits and recommendations
  const {
    usage: limitUsage,
    canCreate: canCreateItinerary,
    recommendations,
    loading: limitsLoading
  } = useItineraryLimits();

  // Use optimized data fetching - only fetch when user is authenticated
  const { 
    data: trips, 
    loading: tripsLoading, 
    error: tripsError,
    refetch: refetchTrips 
  } = useOptimizedFetch('/api/trips', {
    cacheTTL: 2 * 60 * 1000, // 2 minutes cache
    dependencies: [user?.userid],
    enabled: !loadingUser && !!user?.userid // Only fetch when user is loaded and authenticated
  });

  const { 
    data: clients, 
    loading: clientsLoading, 
    error: clientsError,
    refetch: refetchClients 
  } = useOptimizedFetch('/api/clients', {
    cacheTTL: 5 * 60 * 1000, // 5 minutes cache
    dependencies: [user?.userid],
    enabled: !loadingUser && !!user?.userid // Only fetch when user is loaded and authenticated
  });

  const loading = loadingUser || tripsLoading || clientsLoading;

  // Handle errors from optimized fetch
  useEffect(() => {
    if (tripsError) {
      executeWithErrorHandling(() => {
        throw tripsError;
      });
    }
  }, [tripsError, executeWithErrorHandling]);

  useEffect(() => {
    if (clientsError) {
      executeWithErrorHandling(() => {
        throw clientsError;
      });
    }
  }, [clientsError, executeWithErrorHandling]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    
    // Clean up tabs when navigating to dashboard
    if (location.pathname === '/dashboard') {
      cleanupOnNavigation();
    }
  }, [location.pathname]);

  // Calculate dashboard stats with proper field names
  const totalTrips = trips?.length || 0;
  const completedTrips = trips?.filter(
    (trip) => trip.status === "completed"
  ).length || 0;
  const pendingTrips = trips?.filter((trip) => trip.status === "pending").length || 0;
  const confirmedTrips = trips?.filter((trip) => trip.status === "confirmed").length || 0;
  
  // Sort trips by creation date (most recent first) and take first 3
  const recentTrips = trips?.sort((a, b) => {
    const dateA = new Date(a.createdat || 0);
    const dateB = new Date(b.createdat || 0);
    return dateB - dateA; // Most recent first
  }).slice(0, 3) || [];

  // Get unique clients from trips
  const uniqueClients = new Set(trips?.map(trip => trip.clientid) || []).size;
  const totalClients = clients?.length || 0;
  const activeLeads = clients?.filter(c => c.relationship_status === 'lead').length || 0;
  const prospects = clients?.filter(c => c.relationship_status === 'prospect').length || 0;
  const customers = clients?.filter(c => c.relationship_status === 'customer').length || 0;

  // Generate mock commission data
  const mockCommissions = trips?.map(trip => {
    const tripValue = Math.floor(Math.random() * 5000) + 1000;
    const commissionRate = Math.random() * 0.15 + 0.05;
    const commissionAmount = Math.round(tripValue * commissionRate);
    const statuses = ['pending', 'paid', 'cancelled'];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    if (Math.random() < 0.3) return null; // Some trips have no commission
    
    return {
      id: `comm_${trip.tripid}`,
      tripId: trip.tripid,
      commissionAmount,
      status,
      tripValue
    };
  }).filter(Boolean) || [];

  const totalCommissions = mockCommissions.length;
  const pendingCommissions = mockCommissions.filter(c => c.status === 'pending').length;
  const paidCommissions = mockCommissions.filter(c => c.status === 'paid').length;
  const totalEarned = mockCommissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.commissionAmount, 0);
  
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const monthlyEarnings = mockCommissions.filter(c => 
    c.status === 'paid' && new Date() >= thirtyDaysAgo
  ).reduce((sum, c) => sum + c.commissionAmount, 0);

  const convertedThisMonth = clients?.filter(c => 
    c.relationship_status === 'customer' && 
    new Date(c.createdat) >= new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  ).length || 0;

  const conversionRate = totalClients > 0 ? ((customers / totalClients) * 100) : 0;

  // Calculate recent activity with actual timestamps
  const recentActivities = [];
  
  // Most recent client added
  if (clients && clients.length > 0) {
    const mostRecentClient = clients.sort((a, b) => {
      const dateA = new Date(a.createdat || 0);
      const dateB = new Date(b.createdat || 0);
      return dateB - dateA;
    })[0];
    
    if (mostRecentClient?.createdat) {
      const clientDate = new Date(mostRecentClient.createdat);
      const hoursAgo = Math.floor((Date.now() - clientDate.getTime()) / (1000 * 60 * 60));
      const daysAgo = Math.floor(hoursAgo / 24);
      const timeAgo = daysAgo > 0 
        ? `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`
        : hoursAgo > 0 
        ? `${hoursAgo} ${hoursAgo === 1 ? 'hour' : 'hours'} ago`
        : 'Just now';
      
      recentActivities.push({
        type: 'client',
        icon: CheckCircle,
        iconColor: 'green',
        message: 'New client added',
        timestamp: timeAgo,
        date: clientDate
      });
    }
  }

  // Most recent trip created
  if (trips && trips.length > 0) {
    const mostRecentTrip = trips.sort((a, b) => {
      const dateA = new Date(a.createdat || 0);
      const dateB = new Date(b.createdat || 0);
      return dateB - dateA;
    })[0];
    
    if (mostRecentTrip?.createdat) {
      const tripDate = new Date(mostRecentTrip.createdat);
      const hoursAgo = Math.floor((Date.now() - tripDate.getTime()) / (1000 * 60 * 60));
      const daysAgo = Math.floor(hoursAgo / 24);
      const timeAgo = daysAgo > 0 
        ? `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`
        : hoursAgo > 0 
        ? `${hoursAgo} ${hoursAgo === 1 ? 'hour' : 'hours'} ago`
        : 'Just now';
      
      recentActivities.push({
        type: 'trip',
        icon: Calendar,
        iconColor: 'blue',
        message: mostRecentTrip.status?.toLowerCase() === 'confirmed' 
          ? 'Trip confirmed'
          : mostRecentTrip.status?.toLowerCase() === 'completed'
          ? 'Trip completed'
          : 'Trip created',
        timestamp: timeAgo,
        date: tripDate
      });
    }
  }

  // Most recent commission (if any)
  if (mockCommissions.length > 0) {
    const paidCommissions = mockCommissions.filter(c => c.status === 'paid');
    if (paidCommissions.length > 0 && totalEarned > 0) {
      recentActivities.push({
        type: 'commission',
        icon: DollarSign,
        iconColor: 'purple',
        message: 'Commission earned',
        timestamp: 'This month',
        date: new Date()
      });
    }
  }

  // Sort activities by date (most recent first) and take top 4
  const sortedActivities = recentActivities
    .sort((a, b) => b.date - a.date)
    .slice(0, 4);

  // Simple flags to detect whether the agent has any meaningful activity yet
  const hasAnyTrips = totalTrips > 0;
  const hasAnyClients = totalClients > 0;
  const hasAnyCommissions =
    (totalCommissions > 0) ||
    (pendingCommissions > 0) ||
    (totalEarned > 0) ||
    (monthlyEarnings > 0);

  const hasMeaningfulActivity = hasAnyTrips || hasAnyClients || hasAnyCommissions;

  // Status coloring logic
  const getStatusColor = (status) => {
    const isCompleted = status?.toLowerCase() === "completed";
    const isPending = status?.toLowerCase() === "pending" || status === "Pending";
    
    if (isCompleted) return "bg-green-100 text-green-800 border-green-200";
    if (isPending) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  // Capitalize function
  const capitalize = (str) =>
    typeof str === "string" ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  // Handle trip click based on status
  const handleTripClick = (trip) => {
    if (trip.status?.toLowerCase() === "pending") {
      setSelectedPendingTrip(trip);
      setShowPendingTripModal(true);
    } else {
      navigateToUnifiedItinerary(trip.tripid);
    }
  };

  // Format date function
  const formatDate = (dateInput) => {
    if (!dateInput) {
      return "Unknown";
    }
    
    // Handle if dateInput is an object (empty or with properties)
    if (typeof dateInput === 'object') {
      // If it's an empty object, return Unknown
      if (Object.keys(dateInput).length === 0) {
        return "Unknown";
      }
      
      // If it has properties, try to find a date-like property
      const possibleDateProps = ['createdat', 'created_at', 'createdAt', 'date', 'timestamp'];
      for (const prop of possibleDateProps) {
        if (dateInput[prop]) {
          return formatDate(dateInput[prop]); // Recursive call with the actual date value
        }
      }
      
      return "Unknown";
    }
    
    // Handle string input
    if (typeof dateInput !== 'string') {
      return "Unknown";
    }
    
    try {
      // Remove microseconds if present
      let processedDateString = dateInput;
      if (processedDateString.includes('.') && !processedDateString.includes('+') && !processedDateString.includes('Z')) {
        processedDateString = processedDateString.split('.')[0];
      }
      
      const date = new Date(processedDateString);
      
      if (isNaN(date.getTime())) {
        const dateTimeParts = processedDateString.split(' ');
        if (dateTimeParts.length === 2) {
          const isoString = `${dateTimeParts[0]}T${dateTimeParts[1]}`;
          const isoDate = new Date(isoString);
          
          if (!isNaN(isoDate.getTime())) {
            const formatted = isoDate.toLocaleDateString();
            return formatted;
          }
        }
        
        return "Unknown";
      }
      
      const formatted = date.toLocaleDateString();
      return formatted;
    } catch (error) {
      console.error('Error formatting date:', error);
      return "Unknown";
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  // CRM-style metric cards
  const crmMetricCards = [
    {
      title: "Total Clients",
      value: (totalClients || 0).toLocaleString(),
      icon: Users,
      change: "+12%",
      changeType: "positive",
      color: "blue",
      subtitle:
        totalClients && (activeLeads || prospects)
          ? `${activeLeads} leads, ${prospects} prospects`
          : "Add your first client to start tracking your data"
    },
    {
      title: "Active Trips",
      value: (totalTrips || 0).toLocaleString(),
      icon: Calendar,
      change: "+8%",
      changeType: "positive",
      color: "green",
      subtitle: `${pendingTrips} pending, ${completedTrips} completed`
    },
    {
      title: "Total Commissions",
      value: (totalCommissions || 0).toLocaleString(),
      icon: DollarSign,
      change: "+15%",
      changeType: "positive",
      color: "purple",
      subtitle: `$${(totalEarned || 0).toLocaleString()} earned`
    },
    {
      title: "Conversion Rate",
      value: `${(typeof conversionRate === 'number' ? conversionRate : parseFloat(conversionRate) || 0).toFixed(1)}%`,
      icon: TrendingUp,
      change: "+5%",
      changeType: "positive",
      color: "orange",
      subtitle: `${customers} customers, ${convertedThisMonth} this month`
    },
    {
      title: "Pending Commissions",
      value: (pendingCommissions || 0).toLocaleString(),
      icon: Clock,
      change: "+3%",
      changeType: "neutral",
      color: "yellow",
      subtitle: "Awaiting payment"
    },
    {
      title: "Monthly Earnings",
      value: `$${(monthlyEarnings || 0).toLocaleString()}`,
      icon: Target,
      change: "+22%",
      changeType: "positive",
      color: "emerald",
      subtitle: "This month's revenue"
    },
  ];


  // Show loading state while user is being loaded
  if (loadingUser) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Topbar />
        <div className="flex flex-1 min-h-0">
          <SidebarV2 />
          <main className="overflow-auto flex-1 px-3 py-3 w-full min-h-0 bg-gray-50 sm:px-4 sm:py-4">
            <div className="flex justify-center items-center h-full">
              <LoadingSpinner size="lg" text="Loading your dashboard..." />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        <div className="mx-auto max-w-7xl">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="crm-main-content"
            >
            {/* Welcome Section with Itinerary Usage Counter Side-by-Side */}
            <motion.div variants={itemVariants} className="mb-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                {/* Welcome Message - Left Side */}
                <div className="flex-1">
                  <h1 className="mb-1 text-xl font-bold text-gray-900 sm:text-2xl">
                    Welcome back, {user?.firstname || "Agent"}!
                  </h1>
                  <p className="text-sm text-gray-600">
                    Manage your Disney World trips and create magical experiences for your clients
                  </p>
                </div>
                
                {/* Itinerary Usage Counter - Right Side */}
                <div className="flex-shrink-0 w-full lg:w-80 xl:w-96">
                  <UsageDashboard />
                </div>
              </div>
            </motion.div>

            {/* Upgrade Notification Banner */}
            {recommendations && recommendations.should_upgrade && (
              <motion.div variants={itemVariants} className="mb-6">
                <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {canCreateItinerary ? 'Approaching Your Limit' : 'Monthly Limit Reached'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {recommendations.reason}. {canCreateItinerary ? 'Consider upgrading' : 'Upgrade'} to {recommendations.recommended_plan} plan to continue creating itineraries.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/pricing')}
                      className="px-4 py-2 ml-4 text-sm font-medium text-white whitespace-nowrap bg-orange-600 rounded-lg transition-colors hover:bg-orange-700"
                    >
                      Upgrade Now
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Limit Reached Banner (if can't create) */}
            {!canCreateItinerary && !limitsLoading && (
              <motion.div variants={itemVariants} className="mb-6">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Monthly Itinerary Limit Reached
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          You've used all {limitUsage?.monthly_limit} itineraries for this month. Upgrade your plan to create more itineraries.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/pricing')}
                      className="px-4 py-2 ml-4 text-sm font-medium text-white whitespace-nowrap bg-red-600 rounded-lg transition-colors hover:bg-red-700"
                    >
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

              {/* CRM Metrics Grid */}
              <motion.div variants={itemVariants} className="mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 md:gap-6">
                  {crmMetricCards.map((metric, index) => (
                    <motion.div
                      key={metric.title}
                      variants={itemVariants}
                      className="p-4 bg-white rounded-lg border border-gray-200 shadow-md transition-shadow duration-300 hover:shadow-lg"
                      whileHover={{ scale: 1.02 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className="flex items-center mb-2">
                        <div className="flex justify-center items-center w-8 h-8 bg-blue-50 rounded-lg">
                          <metric.icon size={16} className="text-blue-600" />
                        </div>
                      </div>
                      <div className="mb-1 text-xl font-bold text-gray-900">{metric.value}</div>
                      <div className="mb-1 text-xs text-gray-600">{metric.title}</div>
                      <div className="text-xs text-gray-500">{metric.subtitle}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 md:gap-6">
                {/* Recent Trips */}
                <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Trips</h3>
                    <button
                      onClick={() => navigate("/agent/crm/trips")}
                      className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                    >
                      View All
                      <ArrowUpRight size={16} />
                    </button>
                  </div>
                  <div className="p-6">
                    {loading ? (
                      <div className="flex justify-center items-center py-6 sm:py-8">
                        <LoadingSpinner size="lg" text="Loading your trips..." />
                      </div>
                    ) : recentTrips.length === 0 ? (
                      <div className="py-6 text-center sm:py-8">
                        <div className="flex justify-center items-center mx-auto mb-2 w-10 h-10 bg-gray-100 rounded-full sm:w-12 sm:h-12">
                          <Calendar className="w-5 h-5 text-gray-400 sm:w-6 sm:h-6" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-gray-900 sm:text-base">No trips yet</h3>
                        <p className="mb-2 text-xs text-gray-600 sm:text-sm">Create your first Disney World adventure to get started!</p>
                        <button
                          onClick={() => navigate("/agent/crm/trips")}
                          className="inline-flex items-center px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 sm:text-sm"
                        >
                          <Plus className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                          Create Your First Trip
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {recentTrips.map((trip) => (
                          <button
                            key={trip.tripid}
                            className="flex justify-between items-center p-4 w-full text-left bg-white rounded-lg border border-gray-200 shadow-md transition-all duration-300 cursor-pointer hover:shadow-lg"
                            onClick={() => handleTripClick(trip)}
                            type="button"
                          >
                            <div className="flex flex-1 items-center space-x-3 min-w-0">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                trip.status?.toLowerCase() === "completed" 
                                  ? "bg-green-500" 
                                  : trip.status?.toLowerCase() === "pending"
                                  ? "bg-yellow-500"
                                  : "bg-gray-500"
                              }`} />
                              <div className="flex-1 min-w-0">
                                <h4 className="mb-1 text-base font-bold text-gray-900 truncate">
                                  {decodeHtmlEntities(trip.tripname)}
                                </h4>
                                <p className="text-sm text-gray-600 truncate">
                                  {decodeHtmlEntities(trip.fullname) || trip.clientemail || "Client"}
                                </p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-2 text-right">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trip.status)}`}>
                                {capitalize(trip.status)}
                              </span>
                              <p className="mt-1 text-xs text-gray-500">
                                {formatDate(trip.createdat)}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Recent Data Summary */}
                <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                    <button
                      onClick={() => navigate("/agent/crm")}
                      className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                    >
                      View All
                      <ArrowUpRight size={16} />
                    </button>
                  </div>
                  <div className="p-6">
                    {!hasMeaningfulActivity ? (
                      <div className="py-6 text-center sm:py-8">
                        <div className="flex justify-center items-center mx-auto mb-3 w-10 h-10 bg-gray-100 rounded-full sm:w-12 sm:h-12">
                          <Sparkles className="w-5 h-5 text-gray-400 sm:w-6 sm:h-6" />
                        </div>
                        <h3 className="mb-1 text-sm font-semibold text-gray-900 sm:text-base">
                          No activity yet
                        </h3>
                        <p className="mx-auto mb-3 max-w-sm text-xs text-gray-600 sm:text-sm">
                          As you add clients, create trips, and start earning commissions, your recent activity timeline will appear here.
                        </p>
                        <div className="flex flex-col gap-2 items-center sm:flex-row sm:justify-center">
                          <button
                            onClick={() => navigate("/agent/crm/clients")}
                            className="inline-flex items-center px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 sm:text-sm"
                          >
                            <Users className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                            Add Your First Client
                          </button>
                          <button
                            onClick={() => navigate("/agent/crm/trips")}
                            className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100 sm:text-sm"
                          >
                            <Calendar className="mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                            Create Your First Trip
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {sortedActivities.length > 0 ? (
                          sortedActivities.map((activity, index) => {
                            const IconComponent = activity.icon;
                            const iconBgColor = activity.iconColor === 'green' ? 'bg-green-100' :
                                               activity.iconColor === 'blue' ? 'bg-blue-100' :
                                               activity.iconColor === 'purple' ? 'bg-purple-100' :
                                               'bg-orange-100';
                            const iconTextColor = activity.iconColor === 'green' ? 'text-green-600' :
                                                  activity.iconColor === 'blue' ? 'text-blue-600' :
                                                  activity.iconColor === 'purple' ? 'text-purple-600' :
                                                  'text-orange-600';
                            
                            return (
                              <div key={index} className="flex items-center space-x-3">
                                <div className={`flex justify-center items-center w-8 h-8 rounded-full ${iconBgColor}`}>
                                  <IconComponent className={`w-4 h-4 ${iconTextColor}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {activity.message}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {activity.timestamp}
                                  </p>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          // Fallback to summary if no recent activities with dates
                          <>
                            {hasAnyClients && (
                              <div className="flex items-center space-x-3">
                                <div className="flex justify-center items-center w-8 h-8 bg-green-100 rounded-full">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {totalClients === 1 ? "1 client in your CRM" : `${totalClients} clients in your CRM`}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {activeLeads} leads, {prospects} prospects, {customers} customers
                                  </p>
                                </div>
                              </div>
                            )}

                            {hasAnyTrips && (
                              <div className="flex items-center space-x-3">
                                <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                                  <Calendar className="w-4 h-4 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {totalTrips === 1 ? "1 active trip in progress" : `${totalTrips} trips in your workspace`}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {pendingTrips} pending, {completedTrips} completed
                                  </p>
                                </div>
                              </div>
                            )}

                            {hasAnyCommissions && (
                              <div className="flex items-center space-x-3">
                                <div className="flex justify-center items-center w-8 h-8 bg-purple-100 rounded-full">
                                  <DollarSign className="w-4 h-4 text-purple-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {totalEarned > 0
                                      ? `$${totalEarned.toLocaleString()} in commissions recorded`
                                      : `${totalCommissions.toLocaleString()} commission records in your pipeline`}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {pendingCommissions} pending, ${monthlyEarnings.toLocaleString()} this month
                                  </p>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Performance Insights */}
              <motion.div variants={itemVariants} className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Performance Insights</h3>
                  <button
                    onClick={() => navigate("/agent/crm/analytics")}
                    className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
                  >
                    View Analytics
                    <ArrowUpRight size={16} />
                  </button>
                </div>
                <div className="p-6">
                  {!hasMeaningfulActivity ? (
                    <div className="py-6 text-center sm:py-8">
                      <div className="flex justify-center items-center mx-auto mb-3 w-10 h-10 bg-gray-100 rounded-full sm:w-12 sm:h-12">
                        <TrendingUp className="w-5 h-5 text-gray-400 sm:w-6 sm:h-6" />
                      </div>
                      <h3 className="mb-1 text-sm font-semibold text-gray-900 sm:text-base">
                        Performance insights will appear here
                      </h3>
                      <p className="mx-auto mb-0 max-w-sm text-xs text-gray-600 sm:text-sm">
                        Once you have a few clients, trips, and commissions, ParkPro will summarize your performance trends in this section.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div className="flex items-center p-3 space-x-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex justify-center items-center w-8 h-8 bg-gray-200 rounded-lg">
                          <TrendingUp className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Conversion Performance</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {conversionRate.toFixed(1)}%
                          </div>
                          <div className="text-xs text-gray-600">
                            {customers} customers, {convertedThisMonth} new this month
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center p-3 space-x-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex justify-center items-center w-8 h-8 bg-gray-200 rounded-lg">
                          <Clock className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Active Trip Load</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {pendingTrips + confirmedTrips} active
                          </div>
                          <div className="text-xs text-gray-600">
                            {pendingTrips} pending, {confirmedTrips} confirmed
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center p-3 space-x-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex justify-center items-center w-8 h-8 bg-gray-200 rounded-lg">
                          <Star className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Earnings Focus</div>
                          <div className="text-lg font-semibold text-gray-900">
                            ${monthlyEarnings.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-600">commissions this month</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
        </div>
      </div>

      {/* Pending Trip Modal */}
      {showPendingTripModal && selectedPendingTrip && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="p-6 mx-4 w-full max-w-md bg-white rounded-xl shadow-lg">
            <div className="flex items-center mb-4 space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Info className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-black">
                Trip Not Ready Yet
              </h3>
            </div>
            <div className="mb-6">
              <p className="mb-3 text-gray-600">
                The trip "<strong>{decodeHtmlEntities(selectedPendingTrip.tripname)}</strong>" is still pending. 
                Your client needs to complete the questionnaire before you can view the itinerary
              </p>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  <strong>Next Steps:</strong>
                </p>
                <ul className="mt-2 space-y-1 text-sm text-yellow-700">
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>Share the questionnaire link with your client</span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>Wait for them to complete their preferences</span>
                  </li>
                  <li className="flex">
                    <span className="mr-2">•</span>
                    <span>Once completed, the itinerary will be generated automatically and you will be notified via email and notification center</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPendingTripModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-black bg-white rounded-lg border border-gray-400 transition-colors hover:bg-gray-100"
              >
                Got it
              </button>
              <button
                onClick={() => {
                  setShowPendingTripModal(false);
                  navigate("/agent/crm/trips");
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
              >
                Manage Trips
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Trip Modal */}
      {showCreateTripModal && (
        <CreateTripModal
          isOpen={showCreateTripModal}
          onClose={() => setShowCreateTripModal(false)}
          onSuccess={() => {
            setShowCreateTripModal(false);
            // Optionally refresh trips data or show success message
            toast.success("Trip created successfully!");
          }}
        />
      )}

      {/* Pixie Dust Animation */}
      {showAnimation && (
        <PixieDustAnimation
          onComplete={handleAnimationComplete}
          duration={animationDuration}
        />
      )}
    </>
  );
}
